// lessonDeck.js — Feb 2026 · Cycle 69
// Shared deck engine used by:
//   • Teacher Console → Assign & Plan → "Deck / print" (projector deck)
//   • Kid Story Deck page (`/story/deck`) → big-screen story mode
//     with page-by-page read-aloud for parents on the TV
//
// The engine builds a self-contained HTML document (styles + script)
// that is written into a popup or an <iframe>. It supports:
//   • Fullscreen, Print/PDF, Download (HTML file)
//   • Presenter nav: arrow keys, Space/Backspace, PgUp/PgDn, Home/End
//   • Touch swipe navigation
//   • Optional read-aloud (Web Speech API) with a "Read to me" toggle
//   • `kidMode` — hides teacher notes, disables edit affordances,
//     enlarges type, warmer background.
//   • Per-lesson edit persistence via localStorage (teacher mode).

export function presentationSlides(lesson) {
  const slides = [];
  slides.push({
    kind: "title",
    title: lesson.title,
    subtitle: `${lesson.subject || ""}${lesson.uk_year ? ` · Year ${lesson.uk_year}` : ""}`,
  });
  if (lesson.source_objective) {
    slides.push({ kind: "objective", title: "Learning objective", body: lesson.source_objective });
  }
  if (Array.isArray(lesson.steps)) {
    lesson.steps.slice(0, 6).forEach((step, i) => {
      slides.push({
        kind: "step",
        title: `Step ${i + 1}`,
        body: typeof step === "string" ? step : (step.text || step.title || JSON.stringify(step)),
      });
    });
  }
  if (Array.isArray(lesson.pages)) {
    // Story-shaped lessons (pages instead of steps).
    lesson.pages.slice(0, 12).forEach((p, i) => {
      slides.push({
        kind: "page",
        title: p.title || `Page ${i + 1}`,
        body: p.body || p.text || "",
      });
    });
  }
  if (Array.isArray(lesson.frameworks) && lesson.frameworks.length) {
    slides.push({
      kind: "curriculum",
      title: "Curriculum alignment",
      body: lesson.frameworks
        .map((f) => `${f.name}${f.code ? ` (${f.code})` : ""}: ${f.statement || ""}`)
        .join("\n\n"),
    });
  }
  if (Array.isArray(lesson.checkQuestions) || Array.isArray(lesson.questions)) {
    const qs = lesson.checkQuestions || lesson.questions;
    slides.push({
      kind: "check",
      title: "Quick check",
      body: qs
        .slice(0, 3)
        .map((q, i) => `${i + 1}. ${typeof q === "string" ? q : (q.q || q.prompt || "")}`)
        .join("\n"),
    });
  }
  return slides;
}

export function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));
}

export function renderDeckHtml(lesson, slides, opts = {}) {
  const readAloud = opts.readAloud === true;
  const kidMode = opts.kidMode === true;
  const styleTag = `<style>
    :root { --paper:#FBF7EE; --navy:#0F172A; }
    html, body { height: 100%; }
    body { font-family: 'Nunito', system-ui, sans-serif; background: var(--paper); margin:0; scroll-behavior: smooth; }
    .deck { display: flex; overflow-x: auto; scroll-snap-type: x mandatory; scroll-behavior: smooth; -webkit-overflow-scrolling: touch; height: 100vh; }
    .deck::-webkit-scrollbar { display: none; }
    .slide { flex: 0 0 100%; scroll-snap-align: start; min-height: 100vh; padding: 8vh 10vw; box-sizing: border-box; page-break-after: always; display: flex; flex-direction: column; justify-content: center; border-right: 4px solid var(--navy); background: var(--paper); position: relative; }
    .slide.title { background: linear-gradient(135deg, #fef3c7, #a7f3d0); }
    .slide.check { background: #ecfeff; }
    ${kidMode ? `.slide { background: linear-gradient(135deg, #fef3c7, #fce7f3, #a7f3d0); } .slide.title { background: linear-gradient(135deg, #fef08a, #fda4af, #a7f3d0); }` : ""}
    .eyebrow { font-size: 11px; font-weight: 900; text-transform: uppercase; letter-spacing: 3px; color: #64748b; }
    h1 { font-size: clamp(32px, 6vw, 68px); font-weight: 800; margin: 8px 0 8px; color: var(--navy); line-height: 1.05; }
    h2 { font-size: clamp(22px, 3vw, 34px); font-weight: 800; margin: 12px 0 4px; color: var(--navy); }
    p, li { font-size: clamp(18px, 2.4vw, 28px); font-weight: 600; color: #1e293b; line-height: 1.35; white-space: pre-wrap; }
    ${kidMode ? `p { font-size: clamp(22px, 3.2vw, 40px); } h1 { font-size: clamp(38px, 7vw, 78px); }` : ""}
    [contenteditable="true"]:focus { outline: 3px dashed #facc15; outline-offset: 6px; border-radius: 6px; }
    .notes { position: absolute; bottom: 24px; left: 10vw; right: 10vw; border-top: 2px dashed #94a3b8; padding-top: 12px; font-size: 14px; font-weight: 600; color: #475569; min-height: 40px; }
    ${kidMode ? `.notes { display: none; }` : ""}
    .notes-label { display: inline-block; font-size: 10px; font-weight: 900; letter-spacing: 3px; text-transform: uppercase; color: #6366f1; margin-bottom: 4px; }
    .toolbar { position: fixed; top: 12px; right: 12px; z-index: 10; display: flex; gap: 8px; flex-wrap: wrap; }
    .toolbar button { height: 40px; padding: 0 14px; border: 3px solid var(--navy); border-radius: 999px; background: #facc15; font-weight: 800; cursor: pointer; font-family: inherit; font-size: 13px; box-shadow: 0 3px 0 0 var(--navy); }
    .toolbar button.secondary { background: #fff; }
    .toolbar button:active { transform: translateY(1px); box-shadow: 0 1px 0 0 var(--navy); }
    .nav { position: fixed; bottom: 16px; left: 50%; transform: translateX(-50%); z-index: 10; display: flex; gap: 8px; align-items: center; background: #fff; border: 3px solid var(--navy); border-radius: 999px; padding: 6px 10px; box-shadow: 0 4px 0 0 var(--navy); }
    .nav button { height: 40px; padding: 0 18px; border: 2px solid var(--navy); border-radius: 999px; background: #facc15; font-weight: 800; cursor: pointer; font-family: inherit; font-size: 15px; }
    .nav button.secondary { background: #fff; }
    .counter { font-weight: 900; font-size: 12px; color: #0f172a; min-width: 60px; text-align: center; }
    :fullscreen .toolbar { top: 24px; right: 24px; }
    @media print {
      .toolbar, .nav { display: none }
      .deck { display: block; overflow: visible; height: auto; scroll-snap-type: none; }
      .slide { min-height: auto; padding: 6vh 6vw; border-right: none; }
      .notes { position: static; margin-top: 24px; page-break-inside: avoid; }
      [contenteditable] { outline: none !important; }
    }
  </style>`;
  const notesKey = `mwq_deck_notes_${(lesson.id || lesson.title || "").replace(/[^a-z0-9]/gi, "_").slice(0, 64)}`;
  const cfg = JSON.stringify({ readAloud, kidMode });
  const script = `<script>
    (function() {
      var CFG = ${cfg};
      var KEY = ${JSON.stringify(notesKey)};
      var stored = {};
      try { stored = JSON.parse(localStorage.getItem(KEY) || '{}'); } catch(e){}
      Array.prototype.forEach.call(document.querySelectorAll('[data-edit-id]'), function(el){
        var id = el.getAttribute('data-edit-id'); if (stored[id]) el.innerHTML = stored[id];
      });
      var save = function() {
        var out = {};
        Array.prototype.forEach.call(document.querySelectorAll('[data-edit-id]'), function(el){
          out[el.getAttribute('data-edit-id')] = el.innerHTML;
        });
        try { localStorage.setItem(KEY, JSON.stringify(out)); } catch(e){}
      };
      document.addEventListener('input', function(e){ if (e.target.getAttribute && e.target.getAttribute('data-edit-id')) save(); });

      var deck = document.getElementById('mwq-deck');
      var slides = Array.prototype.slice.call(deck.querySelectorAll('.slide'));
      var idx = 0;
      var counter = document.getElementById('mwq-counter');
      var updateCounter = function(){ counter.textContent = (idx + 1) + ' / ' + slides.length; };
      var readCurrentSlide = function(){
        if (!window.speechSynthesis) return;
        window.speechSynthesis.cancel();
        var s = slides[idx];
        var parts = [];
        Array.prototype.forEach.call(s.querySelectorAll('h1, h2, p'), function(el){
          if (el.closest('.notes')) return;
          if (el.textContent.trim()) parts.push(el.textContent.trim());
        });
        if (!parts.length) return;
        var u = new SpeechSynthesisUtterance(parts.join('. '));
        u.rate = CFG.kidMode ? 0.9 : 0.95; u.pitch = 1.0; u.lang = 'en-GB';
        window.speechSynthesis.speak(u);
      };
      var goto = function(n){
        idx = Math.max(0, Math.min(slides.length - 1, n));
        slides[idx].scrollIntoView({ behavior: 'smooth', inline: 'start' });
        updateCounter();
        if (CFG.readAloud) readCurrentSlide();
      };
      updateCounter();
      document.getElementById('mwq-prev').addEventListener('click', function(){ goto(idx - 1); });
      document.getElementById('mwq-next').addEventListener('click', function(){ goto(idx + 1); });

      document.addEventListener('keydown', function(e){
        if (e.target && e.target.getAttribute && e.target.getAttribute('contenteditable') === 'true') return;
        if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') { e.preventDefault(); goto(idx + 1); }
        else if (e.key === 'ArrowLeft' || e.key === 'PageUp' || e.key === 'Backspace') { e.preventDefault(); goto(idx - 1); }
        else if (e.key === 'Home') { e.preventDefault(); goto(0); }
        else if (e.key === 'End') { e.preventDefault(); goto(slides.length - 1); }
        else if (e.key === 'f' || e.key === 'F') { document.getElementById('mwq-fs').click(); }
        else if (e.key === 'p' || e.key === 'P') { document.getElementById('mwq-print').click(); }
        else if (e.key === 'r' || e.key === 'R') { var b = document.getElementById('mwq-read'); if (b) b.click(); }
      });
      // Cycle 69 fallback — also listen on window so remotes and TV
      // dongles that dispatch KeyboardEvents at the window level still
      // advance slides.
      window.addEventListener('keydown', function(e){
        if (e.key === 'ArrowRight' || e.key === 'PageDown') { goto(idx + 1); }
        else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { goto(idx - 1); }
      });
      var sx = null;
      var sy = null;
      deck.addEventListener('touchstart', function(e){ sx = e.touches[0].clientX; sy = e.touches[0].clientY; }, { passive: true });
      deck.addEventListener('touchend', function(e){
        if (sx == null) return;
        var dx = e.changedTouches[0].clientX - sx;
        var dy = e.changedTouches[0].clientY - (sy || 0);
        // Swipe down > 100 px with vertical dominance → ask host to close.
        if (dy > 100 && Math.abs(dy) > Math.abs(dx) * 1.5) {
          sx = null; sy = null;
          try { window.parent && window.parent.postMessage({ type: 'mwq-deck-close' }, '*'); } catch(e) {}
          if (document.fullscreenElement) { try { document.exitFullscreen(); } catch(e){} }
          return;
        }
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) { if (dx < 0) goto(idx + 1); else goto(idx - 1); }
        sx = null; sy = null;
      }, { passive: true });

      var readBtn = document.getElementById('mwq-read');
      // Graceful read-aloud fallback — if the browser has no Speech API,
      // relabel + disable the button so it's clear rather than silently dead.
      if (readBtn && !window.speechSynthesis) {
        readBtn.textContent = '🔇 Read-aloud not available';
        readBtn.setAttribute('disabled', 'disabled');
        readBtn.style.opacity = '0.6';
        readBtn.style.cursor = 'not-allowed';
      }
      if (readBtn && window.speechSynthesis) readBtn.addEventListener('click', function(){
        var synth = window.speechSynthesis;
        if (!synth) return;
        if (synth.speaking) { synth.cancel(); readBtn.textContent = '🔊 Read to me'; }
        else { readCurrentSlide(); readBtn.textContent = '⏸ Stop'; }
      });

      document.getElementById('mwq-fs').addEventListener('click', function(){
        if (!document.fullscreenElement) { (document.documentElement.requestFullscreen||function(){})(); } else { document.exitFullscreen(); }
      });
      document.getElementById('mwq-print').addEventListener('click', function(){ window.print(); });
      document.getElementById('mwq-download').addEventListener('click', function(){
        var html = '<!doctype html><html><head><meta charset="utf-8"><title>' + document.title + '</title></head><body>' + document.body.innerHTML + '</body></html>';
        var blob = new Blob([html], { type: 'text/html' });
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a'); a.href = url; a.download = (document.title || 'deck') + '.html';
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(function(){ URL.revokeObjectURL(url); }, 500);
      });
    })();
  <\/script>`;
  const toolbar = `<div class="toolbar">
    <button id="mwq-fs" title="Toggle fullscreen (F)">${kidMode ? "⛶ Big screen" : "⛶ Fullscreen"}</button>
    <button id="mwq-print" class="secondary" title="Print or save as PDF (P)">🖨 Print / PDF</button>
    <button id="mwq-download" class="secondary" title="Download this deck as an HTML file">⬇ Download</button>
    ${readAloud ? `<button id="mwq-read" class="secondary" title="Read the current slide aloud (R)">🔊 Read to me</button>` : ""}
  </div>`;
  const nav = `<div class="nav" data-testid="deck-nav">
    <button id="mwq-prev" title="Previous (← / PgUp)">← Back</button>
    <span id="mwq-counter" class="counter">1 / ${slides.length}</span>
    <button id="mwq-next" title="Next (→ / PgDn / Space)">Next →</button>
  </div>`;
  const editable = kidMode ? "false" : "true";
  const first = `<section class="slide title" data-slide="0">
    <span class="eyebrow">${kidMode ? "Story time · read aloud" : "My World Quest · Lesson deck"}</span>
    <h1 contenteditable="${editable}" data-edit-id="s0-title">${escapeHtml(lesson.title || "Lesson")}</h1>
    <h2 contenteditable="${editable}" data-edit-id="s0-sub">${escapeHtml(`${lesson.subject || ""}${lesson.uk_year ? ` · Year ${lesson.uk_year}` : ""}`)}</h2>
    ${kidMode ? "" : `<div class="notes"><span class="notes-label">Teacher notes</span><div contenteditable="true" data-edit-id="s0-notes">Tap to add notes for this slide…</div></div>`}
  </section>`;
  const rest = slides.slice(1).map((s, i) => {
    const n = i + 1;
    return `<section class="slide ${s.kind}" data-slide="${n}">
      <span class="eyebrow">${escapeHtml(s.kind)}</span>
      <h1 contenteditable="${editable}" data-edit-id="s${n}-title">${escapeHtml(s.title || "")}</h1>
      <p contenteditable="${editable}" data-edit-id="s${n}-body">${escapeHtml(s.body || "")}</p>
      ${kidMode ? "" : `<div class="notes"><span class="notes-label">Teacher notes</span><div contenteditable="true" data-edit-id="s${n}-notes">Tap to add notes for this slide…</div></div>`}
    </section>`;
  }).join("");
  return `${styleTag}${toolbar}<div id="mwq-deck" class="deck">${first}${rest}</div>${nav}${script}`;
}
